// src/pages/babilonia/auditoria-admin.js
//
// ADMINISTRACIÓN > AUDITORÍA DE USO
// Permiso: "babilonia-auditoria-admin"
//
// La sesión de Babilonia se queda abierta hasta que el usuario la cierra,
// así que el inicio de sesión NO es un buen indicador de uso. Esta pantalla
// mide ACTIVIDAD: cualquier acción registrada (abrir una sección, iniciar o
// cerrar sesión). Con eso se sabe quién usa el sistema, cuántos días del
// periodo tuvo movimiento y cuánto tiempo lleva sin usarlo.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const API_BASE_URL = "https://api.checkmark.mx/";
const ACCESS_TOKEN =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const BITACORA_POR_PAGINA = 50;

// Nombre amigable de cada ruta del sistema (si no está aquí, se muestra la ruta).
const SECCIONES = {
  "/analytics": "Inicio",
  "/form/crear-venta": "Ventas",
  "/deals/request": "Solicitudes",
  "/deals/requests": "Resumen de solicitudes",
  "/deals/ranking": "Ranking",
  "/deals/update": "Actualizar solicitud",
  "/form/ventas": "Ventas totales",
  "/pos/customer-order-v2": "Iniciar proceso",
  "/rendimiento": "Rendimiento",
  "/pages/register": "Registro de usuarios",
  "/babilonia/catalogo-admin": "Catálogo Babilonia",
  "/babilonia/usuarios-admin": "Usuarios Babilonia",
  "/babilonia/agencias-cotizador-admin": "Cotizador por agencia",
  "/babilonia/export-solicitudes": "Exportar solicitudes",
  "/babilonia/auditoria": "Auditoría de uso",
  "/form/crear-venta-nr": "Ventas NR",
  "/form/prodesk-nr": "Prodesk NR",
  "/facturacion/comparativo": "Comparativo Excel",
  "/facturacion/facturas": "Carga de facturas",
  "/facturacion/graficas": "Otros ingresos",
};

const EVENTOS = {
  navegacion: { label: "Abrió sección", className: "au-ev-nav" },
  login_ok: { label: "Inició sesión", className: "au-ev-ok" },
  logout: { label: "Cerró sesión", className: "au-ev-out" },
  login_fallido: { label: "Intento fallido", className: "au-ev-fail" },
};

const NIVELES = {
  alto: { label: "Alto", className: "au-nivel-alto" },
  medio: { label: "Medio", className: "au-nivel-medio" },
  bajo: { label: "Bajo", className: "au-nivel-bajo" },
  sin_uso: { label: "Sin actividad", className: "au-nivel-sin" },
};

const FILTROS_ULTIMA = {
  "": "Cualquier fecha",
  reciente: "En los últimos 7 días",
  "7": "Hace 7 días o más",
  "30": "Hace 30 días o más",
  nunca: "Sin actividad registrada",
};

// Textos de los tooltips (en un solo lugar para mantenerlos consistentes).
const TIP = {
  conActividad:
    "Usuarios que hicieron al menos una acción en el periodo: abrir una sección, iniciar o cerrar sesión. No importa si su sesión ya estaba abierta desde antes.",
  sinActividadPeriodo:
    "Usuarios dentro de los filtros que no registraron ninguna acción entre las fechas seleccionadas.",
  sin30:
    "Usuarios cuya última acción fue hace 30 días o más, contando desde hoy y sin importar el periodo elegido. Incluye a quienes no tienen ninguna actividad registrada.",
  promDias:
    "Promedio de días con al menos una acción, solo entre los usuarios que sí tuvieron actividad. Se compara contra todos los días del periodo, incluidos fines de semana.",
  acciones:
    "Total de acciones registradas en el periodo: secciones abiertas, inicios y cierres de sesión. No incluye intentos fallidos.",
  nivel:
    "Qué tan seguido entra el usuario según sus días con actividad sobre el total de días del periodo. Alto: 50% o más. Medio: 20% a 49%. Bajo: menos de 20%. Sin actividad: ningún día.",
  ultima:
    "Fecha y hora de la acción más reciente del usuario, sin importar el periodo elegido. Es la mejor forma de saber cuánto tiempo lleva sin usar su cuenta.",
  dias:
    "Días distintos del periodo en los que el usuario hizo al menos una acción, sobre el total de días del periodo, incluidos fines de semana.",
  accionesUsuario:
    "Acciones del usuario en el periodo: secciones abiertas, inicios y cierres de sesión.",
  secciones:
    "Cuántas secciones diferentes abrió en el periodo. Ayuda a ver si usa todo el sistema o solo una parte.",
  logins:
    "Veces que escribió usuario y contraseña. Como la sesión se mantiene abierta, un número bajo no significa que no use el sistema.",
  fallidos:
    "Intentos de inicio de sesión con contraseña incorrecta. Varios seguidos pueden indicar que olvidó su contraseña.",
  porDia:
    "Cuántos usuarios distintos tuvieron actividad cada día. Pasa el cursor sobre una barra para ver el detalle.",
  porHora:
    "En qué horas del día se concentra la actividad. Entre más oscuro, más acciones en esa hora.",
  topSecciones:
    "Secciones que más se abrieron en el periodo y cuántos usuarios distintos las usaron.",
  agenciaPct:
    "Porcentaje de los usuarios de la agencia que tuvieron al menos una acción en el periodo.",
  agenciaMulti:
    "Un usuario asignado a varias agencias cuenta en cada una de ellas.",
  estatus:
    "Estatus de la cuenta en Usuarios Babilonia. Por defecto solo se muestran las cuentas activas.",
  bitacora:
    "Detalle de cada acción registrada, de la más reciente a la más antigua.",
};

// ---------- utilidades ----------

const pad = (n) => String(n).padStart(2, "0");

const toInputDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const hoy = () => toInputDate(new Date());

const haceDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return toInputDate(d);
};

const inicioDeMes = () => {
  const d = new Date();
  return toInputDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const parseFecha = (value) => {
  if (!value) return null;
  const [f, h = "00:00:00"] = String(value).split(" ");
  const [y, m, d] = f.split("-").map(Number);
  const [hh, mm, ss] = h.split(":").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0);
};

const formatFechaHora = (value) => {
  const d = parseFecha(value);
  if (!d) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const formatDiaCorto = (value) => {
  const d = parseFecha(value);
  if (!d) return "";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
};

const textoHace = (dias) => {
  if (dias === null || dias === undefined) return "Sin registro";
  if (dias === 0) return "Hoy";
  if (dias === 1) return "Ayer";
  if (dias < 30) return `Hace ${dias} días`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? "Hace 1 mes" : `Hace ${meses} meses`;
};

// Días naturales del periodo (incluye fines de semana y festivos).
const contarDiasPeriodo = (inicio, fin) => {
  const a = parseFecha(inicio);
  const b = parseFecha(fin);
  if (!a || !b || a > b) return 1;
  return Math.max(Math.round((b - a) / 86400000) + 1, 1);
};

const calcularNivel = (diasActivos, diasPeriodo) => {
  if (!diasActivos) return "sin_uso";
  const ratio = diasActivos / diasPeriodo;
  if (ratio >= 0.5) return "alto";
  if (ratio >= 0.2) return "medio";
  return "bajo";
};

const cumpleFiltroUltima = (u, filtro) => {
  const d = u.dias_sin_actividad;
  if (!filtro) return true;
  if (filtro === "nunca") return d === null;
  if (filtro === "reciente") return d !== null && d < 7;
  const minimo = Number(filtro);
  return d === null || d >= minimo;
};

// Busca primero la ruta exacta y luego por prefijo, para rutas con
// parámetros (por ejemplo /deals/update/123 -> "Actualizar solicitud").
// Promedios: sin decimal si es entero (2 en vez de 2.0), con uno si no (1.7).
const formatPromedio = (valor) => {
  const redondeado = Math.round((valor || 0) * 10) / 10;
  return Number.isInteger(redondeado) ? String(redondeado) : redondeado.toFixed(1);
};

const PRESETS = [
  { key: "hoy", label: "Hoy" },
  { key: "7", label: "Últimos 7 días" },
  { key: "30", label: "Últimos 30 días" },
  { key: "mes", label: "Mes actual" },
  { key: "90", label: "Últimos 90 días" },
];

const rangoDePreset = (key) => {
  const dias = { hoy: 0, "7": 6, "30": 29, "90": 89 };
  return {
    inicio: key === "mes" ? inicioDeMes() : haceDias(dias[key]),
    fin: hoy(),
  };
};

const RANGO_NIVEL = { sin_uso: 0, bajo: 1, medio: 2, alto: 3 };

// Ordena una lista por el campo indicado. Texto: alfabético (es-MX).
// Números: de menor a mayor. Los vacíos (null) siempre quedan al final.
const ordenarLista = (lista, { campo, dir }) => {
  const factor = dir === "asc" ? 1 : -1;
  return [...lista].sort((a, b) => {
    const va = a[campo];
    const vb = b[campo];
    const vaVacio = va === null || va === undefined || va === "";
    const vbVacio = vb === null || vb === undefined || vb === "";
    if (vaVacio && vbVacio) return 0;
    if (vaVacio) return 1;
    if (vbVacio) return -1;
    if (typeof va === "number" && typeof vb === "number") {
      return factor * (va - vb);
    }
    return factor * String(va).localeCompare(String(vb), "es-MX", { sensitivity: "base" });
  });
};

// Al dar clic en una columna nueva: texto empieza A-Z, números de mayor a menor.
const siguienteOrden = (prev, campo, esTexto) =>
  prev.campo === campo
    ? { campo, dir: prev.dir === "asc" ? "desc" : "asc" }
    : { campo, dir: esTexto ? "asc" : "desc" };

const nombreSeccion = (ruta) => {
  if (!ruta) return "—";
  if (SECCIONES[ruta]) return SECCIONES[ruta];
  const prefijo = Object.keys(SECCIONES)
    .filter((key) => ruta.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0];
  return prefijo ? SECCIONES[prefijo] : ruta;
};

const iniciales = (nombre = "") =>
  String(nombre)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase() || "?";

const postJson = async (endpoint, body) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token: ACCESS_TOKEN, ...body }),
  });
  const result = await response.json();
  if (result.statusCode !== 200) {
    throw new Error(result.message || "No se pudo obtener la información.");
  }
  return result.data;
};

// Ícono "i" con tooltip. Funciona con mouse y con teclado (Tab).
// pos: "top" (default) | "bottom" (para encabezados de tabla) ; align: "center" | "left" | "right"
const Info = ({ texto, pos = "top", align = "center" }) => (
  <span
    className={`au-info au-info-${pos} au-info-${align}`}
    tabIndex={0}
    role="img"
    aria-label={texto}
    data-tip={texto}
    onClick={(e) => e.stopPropagation()}
  >
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
      <circle cx="8" cy="8" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="4.9" r="1" fill="currentColor" />
      <rect x="7.2" y="6.9" width="1.6" height="4.6" rx="0.8" fill="currentColor" />
    </svg>
  </span>
);

// ---------- componente ----------

function BabiloniaAuditoriaAdmin() {
  // Filtros que van al servidor
  const [fechaInicio, setFechaInicio] = useState(haceDias(29));
  const [fechaFin, setFechaFin] = useState(hoy());
  // Atajo de fechas seleccionado (se resalta). Se limpia si se editan las fechas a mano.
  const [presetSel, setPresetSel] = useState("30");
  const [agencia, setAgencia] = useState("");
  const [estatus, setEstatus] = useState("activos");

  // Filtros locales de la tabla de usuarios
  const [buscar, setBuscar] = useState("");
  const [nivel, setNivel] = useState("");
  const [filtroUltima, setFiltroUltima] = useState("");
  const [orden, setOrden] = useState({ campo: "dias_activos", dir: "desc" });
  const [ordenAg, setOrdenAg] = useState({ campo: "pct", dir: "desc" });
  const [ordenBit, setOrdenBit] = useState({ campo: "fecha", dir: "desc" });

  const [tab, setTab] = useState("usuarios");
  const [agencias, setAgencias] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);

  // Bitácora
  const [bitUsuario, setBitUsuario] = useState(null);
  const [bitEvento, setBitEvento] = useState("");
  const [bitBuscar, setBitBuscar] = useState("");
  const [bitBuscarAplicado, setBitBuscarAplicado] = useState("");
  const [bitPagina, setBitPagina] = useState(0);
  const [bitacora, setBitacora] = useState({ total: 0, eventos: [] });
  const [bitLoading, setBitLoading] = useState(false);

  const agenciasMap = useMemo(() => {
    const map = {};
    agencias.forEach((a) => {
      map[String(a.ag_id)] = a.nombre;
    });
    return map;
  }, [agencias]);

  const filtrosServidor = useMemo(
    () => ({ fecha_inicio: fechaInicio, fecha_fin: fechaFin, agencia, estatus }),
    [fechaInicio, fechaFin, agencia, estatus],
  );

  useEffect(() => {
    fetch(`${API_BASE_URL}agencies?access_token=${ACCESS_TOKEN}&group=GoCar`)
      .then((r) => r.json())
      .then((result) => {
        if (result.statusCode === 200) {
          const lista = [...(result?.data?.agencies || [])].sort((a, b) =>
            String(a.nombre).localeCompare(String(b.nombre)),
          );
          setAgencias(lista);
        }
      })
      .catch(() => {});
  }, []);

  const cargarResumen = useCallback(async () => {
    setLoading(true);
    try {
      const data = await postJson("financiamiento/auditoria/resumen", filtrosServidor);
      setResumen(data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo cargar la auditoría.",
      });
    } finally {
      setLoading(false);
    }
  }, [filtrosServidor]);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  const cargarBitacora = useCallback(async () => {
    setBitLoading(true);
    try {
      const data = await postJson("financiamiento/auditoria/eventos", {
        ...filtrosServidor,
        id_usuario: bitUsuario ? bitUsuario.id : "",
        evento: bitEvento,
        buscar: bitBuscarAplicado,
        orden: ordenBit.campo,
        dir: ordenBit.dir,
        limit: BITACORA_POR_PAGINA,
        offset: bitPagina * BITACORA_POR_PAGINA,
      });
      setBitacora({ total: data.total || 0, eventos: data.eventos || [] });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo cargar la bitácora.",
      });
    } finally {
      setBitLoading(false);
    }
  }, [filtrosServidor, bitUsuario, bitEvento, bitBuscarAplicado, bitPagina, ordenBit]);

  useEffect(() => {
    if (tab === "bitacora") cargarBitacora();
  }, [tab, cargarBitacora]);

  // Si cambian filtros, la bitácora regresa a la primera página
  // (con update funcional React no re-renderiza si ya estaba en 0)
  useEffect(() => {
    setBitPagina((p) => (p === 0 ? p : 0));
  }, [filtrosServidor, bitUsuario, bitEvento, bitBuscarAplicado, ordenBit]);

  // ---------- derivados ----------

  const diasPeriodo = useMemo(
    () => contarDiasPeriodo(fechaInicio, fechaFin),
    [fechaInicio, fechaFin],
  );

  const usuarios = useMemo(() => {
    const lista = resumen?.por_usuario || [];
    return lista.map((u) => ({
      ...u,
      nivel: calcularNivel(u.dias_activos, diasPeriodo),
      agenciasNombres: (u.agencias || []).map((id) => agenciasMap[String(id)] || id),
    })).map((u) => ({
      ...u,
      nivel_rango: RANGO_NIVEL[u.nivel],
      agencias_texto: [...u.agenciasNombres].sort().join(", "),
      // "Sin registro" se ordena como lo más antiguo
      dias_sin_actividad_orden:
        u.dias_sin_actividad === null ? Number.MAX_SAFE_INTEGER : u.dias_sin_actividad,
    }));
  }, [resumen, diasPeriodo, agenciasMap]);

  const kpis = useMemo(() => {
    const total = usuarios.length;
    const conActividad = usuarios.filter((u) => u.dias_activos > 0).length;
    const acciones = usuarios.reduce((s, u) => s + u.acciones, 0);
    const sin30 = usuarios.filter(
      (u) => u.dias_sin_actividad === null || u.dias_sin_actividad >= 30,
    ).length;
    const promDias = conActividad
      ? usuarios.reduce((s, u) => s + u.dias_activos, 0) / conActividad
      : 0;

    return {
      total,
      conActividad,
      sinActividad: total - conActividad,
      pct: total ? Math.round((conActividad / total) * 100) : 0,
      acciones,
      sin30,
      promDias,
    };
  }, [usuarios]);

  const usuariosFiltrados = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const lista = usuarios.filter((u) => {
      if (nivel && u.nivel !== nivel) return false;
      if (!cumpleFiltroUltima(u, filtroUltima)) return false;
      if (!q) return true;
      return (
        u.nombre.toLowerCase().includes(q) ||
        u.correo.toLowerCase().includes(q) ||
        u.agenciasNombres.some((n) => String(n).toLowerCase().includes(q))
      );
    });

    return ordenarLista(lista, orden);
  }, [usuarios, buscar, nivel, filtroUltima, orden]);

  const resumenAgencias = useMemo(() => {
    const map = {};
    usuarios.forEach((u) => {
      const ids =
        agencia !== ""
          ? [agencia]
          : (u.agencias || []).length
          ? u.agencias
          : ["__sin__"];
      ids.forEach((id) => {
        const key = String(id);
        if (!map[key]) {
          map[key] = {
            ag_id: key,
            nombre: key === "__sin__" ? "Sin agencia asignada" : agenciasMap[key] || key,
            usuarios: 0,
            conActividad: 0,
            sin30: 0,
            acciones: 0,
            diasActivos: 0,
          };
        }
        const r = map[key];
        r.usuarios += 1;
        if (u.dias_activos > 0) r.conActividad += 1;
        if (u.dias_sin_actividad === null || u.dias_sin_actividad >= 30) r.sin30 += 1;
        r.acciones += u.acciones;
        r.diasActivos += u.dias_activos;
      });
    });
    return Object.values(map)
      .map((r) => ({
        ...r,
        pct: r.usuarios ? Math.round((r.conActividad / r.usuarios) * 100) : 0,
        promDias: r.conActividad ? r.diasActivos / r.conActividad : 0,
      }));
  }, [usuarios, agencia, agenciasMap]);

  const agenciasOrdenadas = useMemo(
    () => ordenarLista(resumenAgencias, ordenAg),
    [resumenAgencias, ordenAg],
  );

  const porDia = resumen?.por_dia || [];
  const maxDia = Math.max(1, ...porDia.map((d) => d.usuarios));
  const porHora = resumen?.por_hora || [];
  const maxHora = Math.max(1, ...porHora);
  const topSecciones = resumen?.top_secciones || [];
  const maxSeccion = Math.max(1, ...topSecciones.map((s) => s.visitas));

  // ---------- acciones ----------

  const aplicarPreset = (preset) => {
    const { inicio, fin } = rangoDePreset(preset);
    setFechaInicio(inicio);
    setFechaFin(fin);
    setPresetSel(preset);
  };


  const verBitacoraDe = (usuario) => {
    setBitUsuario({ id: usuario.id, nombre: usuario.nombre });
    setTab("bitacora");
  };

  const limpiarFiltros = () => {
    aplicarPreset("30");
    setAgencia("");
    setEstatus("activos");
    setBuscar("");
    setNivel("");
    setFiltroUltima("");
  };

  const exportarUsuarios = () => {
    const filas = usuariosFiltrados.map((u) => ({
      ID: u.id,
      Nombre: u.nombre,
      Correo: u.correo,
      Estatus: u.activo === 1 ? "Activo" : "Inactivo",
      Agencias: u.agenciasNombres.join(", "),
      "Nivel de uso": NIVELES[u.nivel].label,
      "Última actividad": u.ultima_actividad ? formatFechaHora(u.ultima_actividad) : "Sin registro",
      "Días sin actividad": u.dias_sin_actividad ?? "",
      "Días con actividad": u.dias_activos,
      "Días del periodo": diasPeriodo,
      Acciones: u.acciones,
      "Secciones distintas": u.secciones_distintas,
      "Inicios de sesión": u.logins_ok,
      "Intentos fallidos": u.logins_fallidos,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), "Por usuario");

    const filasAg = agenciasOrdenadas.map((r) => ({
      Agencia: r.nombre,
      Usuarios: r.usuarios,
      "Con actividad": r.conActividad,
      "% con actividad": r.pct,
      "Sin actividad 30+ días": r.sin30,
      "Prom. días con actividad": Number(r.promDias.toFixed(1)),
      Acciones: r.acciones,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasAg), "Por agencia");

    XLSX.writeFile(wb, `auditoria_babilonia_${fechaInicio}_a_${fechaFin}.xlsx`);
  };

  const exportarBitacora = async () => {
    try {
      Swal.fire({
        title: "Generando bitácora...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const data = await postJson("financiamiento/auditoria/eventos", {
        ...filtrosServidor,
        id_usuario: bitUsuario ? bitUsuario.id : "",
        evento: bitEvento,
        buscar: bitBuscarAplicado,
        orden: ordenBit.campo,
        dir: ordenBit.dir,
        limit: 5000,
        offset: 0,
      });
      const filas = (data.eventos || []).map((e) => ({
        Fecha: formatFechaHora(e.created_at),
        Usuario: e.nombre || "(no registrado)",
        Correo: e.correo || "",
        Acción: EVENTOS[e.evento]?.label || e.evento,
        Detalle: e.evento === "navegacion" ? nombreSeccion(e.detalle) : e.detalle || "",
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), "Bitácora");
      XLSX.writeFile(wb, `bitacora_babilonia_${fechaInicio}_a_${fechaFin}.xlsx`);
      Swal.close();
      if ((data.total || 0) > 5000) {
        Swal.fire({
          icon: "info",
          title: "Exportación parcial",
          text: `Se exportaron las 5,000 acciones más recientes de ${data.total}. Reduce el rango de fechas para obtener el resto.`,
        });
      }
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.message });
    }
  };

  const totalPaginas = Math.max(1, Math.ceil((bitacora.total || 0) / BITACORA_POR_PAGINA));

  // Encabezado de tabla. Si recibe "campo", se puede ordenar con clic o
  // con teclado (Enter/Espacio). "texto" indica si el campo es alfabético.
  const Th = ({ campo, texto, sort, onSort, children, tip, num, align = "center" }) => {
    const activo = campo && sort && sort.campo === campo;
    const cambiar = () => onSort((prev) => siguienteOrden(prev, campo, texto));
    return (
      <th
        className={`${campo ? "au-th-sort" : ""} ${activo ? "au-th-activo" : ""} ${num ? "au-num" : ""}`}
        onClick={campo ? cambiar : undefined}
        onKeyDown={
          campo
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  cambiar();
                }
              }
            : undefined
        }
        tabIndex={campo ? 0 : undefined}
        aria-sort={activo ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
      >
        <span className="au-th-inner">
          {children}
          {campo && (
            <span className="au-sort-icon" aria-hidden="true">
              <svg viewBox="0 0 10 14" width="9" height="12">
                <path d="M5 1 L9 5.5 H1 Z" className={activo && sort.dir === "asc" ? "on" : ""} />
                <path d="M5 13 L1 8.5 H9 Z" className={activo && sort.dir === "desc" ? "on" : ""} />
              </svg>
            </span>
          )}
          {tip && <Info texto={tip} pos="bottom" align={align} />}
        </span>
      </th>
    );
  };

  // ---------- render ----------

  return (
    <>
      <style>
        {`
          .au-page { max-width: 1550px; margin: 0 auto; color: #0f172a; }

          .au-head {
            display: flex; flex-wrap: wrap; gap: 16px;
            align-items: flex-end; justify-content: space-between;
            padding: 6px 2px 18px;
          }
          .au-title { font-weight: 800; letter-spacing: -0.03em; margin: 0; }
          .au-subtitle { color: #475569; font-size: 14px; margin-top: 6px; max-width: 78ch; line-height: 1.5; }

          .au-panel {
            background: #fff; border: 1px solid #e2e8f0; border-radius: 18px;
            padding: 18px; margin-bottom: 16px;
          }

          .au-control {
            border-radius: 12px; border: 1px solid #dbe3ee;
            min-height: 40px; font-size: 14px;
          }
          .au-control:focus {
            border-color: #004987;
            box-shadow: 0 0 0 0.18rem rgba(0, 73, 135, 0.14);
          }
          .au-label { font-size: 12px; color: #64748b; margin-bottom: 4px; display: flex; align-items: center; }

          .au-presets { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
          .au-chip {
            border: 1px solid #dbe3ee; background: #f8fafc; color: #334155;
            border-radius: 999px; padding: 4px 12px; font-size: 12px; cursor: pointer;
          }
          .au-chip:hover { border-color: #004987; color: #004987; }
          .au-chip-activo, .au-chip-activo:hover {
            background: #004987; border-color: #004987; color: #fff; font-weight: 600;
          }
          .au-chip:focus-visible { outline: 2px solid #004987; outline-offset: 2px; }

          .au-notice.au-notice-off { border-left-color: #dc2626; background: #fef2f2; color: #7f1d1d; }
          .au-notice {
            border-left: 3px solid #f59e0b; background: #fffbeb; color: #78350f;
            border-radius: 10px; padding: 10px 14px; font-size: 13px; margin-bottom: 16px;
          }

          /* Tooltip */
          .au-info {
            position: relative; display: inline-flex; align-items: center; justify-content: center;
            width: 15px; height: 15px; margin-left: 6px; border-radius: 50%;
            flex: 0 0 auto; line-height: 0; opacity: .55;
            cursor: help; vertical-align: middle;
            text-transform: none; letter-spacing: 0;
          }
          .au-info:hover, .au-info:focus { opacity: 1; outline: none; }
          .au-info:focus-visible { box-shadow: 0 0 0 2px #93c5fd; }
          .au-info svg { display: block; }
          .au-info::after {
            content: attr(data-tip);
            position: absolute; z-index: 30;
            width: max-content; max-width: 270px;
            background: #0f172a; color: #f8fafc;
            font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
            font-size: 12px; font-weight: 400; font-style: normal; line-height: 1.45;
            text-align: left; white-space: normal;
            padding: 8px 10px; border-radius: 8px;
            box-shadow: 0 8px 24px rgba(15, 23, 42, .25);
            opacity: 0; pointer-events: none; transition: opacity .12s ease;
          }
          .au-info:hover::after, .au-info:focus::after { opacity: 1; }
          .au-info-top::after { bottom: calc(100% + 8px); }
          .au-info-bottom::after { top: calc(100% + 8px); }
          .au-info-center::after { left: 50%; transform: translateX(-50%); }
          .au-info-left::after { left: -6px; }
          .au-info-right::after { right: -6px; }

          /* Franja de indicadores */
          .au-kpis {
            display: grid; grid-template-columns: repeat(5, minmax(0, 1fr));
            border: 1px solid #e2e8f0; border-radius: 18px; background: #fff;
            margin-bottom: 16px;
          }
          .au-kpi { padding: 16px 18px; border-left: 1px solid #edf2f7; }
          .au-kpi:first-child { border-left: 0; border-radius: 17px 0 0 17px; }
          .au-kpi-label { font-size: 12px; color: #64748b; display: flex; align-items: center; }
          .au-kpi-value { font-size: 28px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; margin-top: 4px; }
          .au-kpi-value small { font-size: 15px; font-weight: 600; color: inherit; opacity: .7; }
          .au-kpi-note { font-size: 12px; color: #64748b; margin-top: 2px; }
          .au-kpi-alert .au-kpi-value { color: #b91c1c; }
          .au-kpi-main { background: #004987; color: #fff; }
          .au-kpi-main .au-kpi-label, .au-kpi-main .au-kpi-note { color: rgba(255,255,255,.8); }
          @media (max-width: 991px) {
            .au-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .au-kpi { border-left: 0; border-top: 1px solid #edf2f7; }
            .au-kpi:first-child { border-top: 0; border-radius: 17px 17px 0 0; grid-column: 1 / -1; }
          }

          .au-grid-2 { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; }
          @media (max-width: 1199px) { .au-grid-2 { grid-template-columns: 1fr; } }
          .au-panel-title { font-weight: 750; font-size: 15px; margin: 0 0 12px; display: flex; align-items: center; }
          .au-subhead { font-weight: 700; font-size: 13px; margin: 20px 0 8px; display: flex; align-items: center; color: #334155; }

          .au-bars {
            display: flex; align-items: flex-end; gap: 3px; height: 150px;
            border-bottom: 1px solid #e2e8f0; overflow-x: auto; padding-top: 6px;
          }
          .au-bar-col { flex: 1 0 8px; display: flex; flex-direction: column; justify-content: flex-end; height: 100%; }
          .au-bar { background: #004987; border-radius: 4px 4px 0 0; min-height: 2px; }
          .au-bar-zero { background: #e2e8f0; }
          .au-bar-col:hover .au-bar { background: #0ea5e9; }
          .au-bars-axis { display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; margin-top: 6px; }

          .au-hours { display: grid; grid-template-columns: repeat(24, 1fr); gap: 3px; }
          .au-hour { height: 26px; border-radius: 5px; }
          .au-hours-axis { display: grid; grid-template-columns: repeat(24, 1fr); font-size: 10px; color: #94a3b8; margin-top: 4px; }

          .au-sec-row { display: grid; grid-template-columns: 1fr auto; gap: 4px 10px; font-size: 13px; margin-bottom: 10px; }
          .au-sec-track { grid-column: 1 / -1; height: 6px; background: #f1f5f9; border-radius: 99px; overflow: hidden; }
          .au-sec-fill { height: 100%; background: #0ea5e9; border-radius: 99px; }
          .au-sec-count { color: #64748b; font-variant-numeric: tabular-nums; }

          .au-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; flex-wrap: wrap; }
          .au-tab {
            background: none; border: 0; border-bottom: 2px solid transparent;
            padding: 10px 14px; font-weight: 650; color: #64748b; font-size: 14px;
          }
          .au-tab.active { color: #004987; border-bottom-color: #004987; }
          .au-tab:focus-visible { outline: 2px solid #004987; outline-offset: -2px; }

          .au-table-wrap { max-height: calc(100vh - 300px); overflow: auto; }
          .au-table { margin-bottom: 0; font-size: 13px; }
          .au-table thead th {
            position: sticky; top: 0; z-index: 2; background: #fff;
            color: #475569; font-size: 12px; font-weight: 700;
            border-bottom: 1px solid #e2e8f0; padding: 12px 14px; white-space: nowrap;
          }
          .au-th-sort { cursor: pointer; user-select: none; }
          .au-th-sort:hover, .au-th-activo { color: #004987; }
          .au-th-sort:focus-visible { outline: 2px solid #004987; outline-offset: -2px; }
          .au-th-inner { display: inline-flex; align-items: center; gap: 4px; }
          .au-num .au-th-inner { justify-content: flex-end; }
          .au-sort-icon { display: inline-flex; }
          .au-sort-icon svg { display: block; }
          .au-sort-icon path { fill: #cbd5e1; }
          .au-sort-icon path.on { fill: #004987; }
          .au-table tbody td { padding: 12px 14px; vertical-align: middle; border-color: #edf2f7; }
          .au-table tbody tr:hover { background: #f8fbff; }
          .au-num { text-align: right; font-variant-numeric: tabular-nums; }

          .au-user { display: flex; align-items: center; gap: 10px; min-width: 230px; }
          .au-avatar {
            width: 36px; height: 36px; border-radius: 12px; flex: 0 0 auto;
            background: #004987; color: #fff; font-weight: 800; font-size: 13px;
            display: flex; align-items: center; justify-content: center;
          }
          .au-avatar-off { background: #94a3b8; }
          .au-user-name { font-weight: 700; line-height: 1.2; }
          .au-user-mail { color: #64748b; font-size: 12px; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

          .au-agencias { color: #475569; font-size: 12px; max-width: 220px; }

          .au-pill { display: inline-block; border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 650; white-space: nowrap; }
          .au-nivel-alto { background: #dcfce7; color: #166534; }
          .au-nivel-medio { background: #e0f2fe; color: #075985; }
          .au-nivel-bajo { background: #fef3c7; color: #92400e; }
          .au-nivel-sin { background: #fee2e2; color: #991b1b; }
          .au-ev-ok { background: #dcfce7; color: #166534; }
          .au-ev-fail { background: #fee2e2; color: #991b1b; }
          .au-ev-out { background: #f1f5f9; color: #475569; }
          .au-ev-nav { background: #e0f2fe; color: #075985; }

          .au-alerta { color: #b91c1c; font-weight: 650; }
          .au-muted { color: #94a3b8; }

          .au-link { background: none; border: 0; padding: 0; color: #004987; font-weight: 650; font-size: 12px; white-space: nowrap; }
          .au-link:hover { text-decoration: underline; }

          .au-filter-tag {
            display: inline-flex; align-items: center; gap: 8px; background: #eff6ff;
            color: #1e3a8a; border-radius: 999px; padding: 4px 6px 4px 12px; font-size: 13px;
          }
          .au-filter-tag button {
            border: 0; background: #dbeafe; color: #1e3a8a; border-radius: 999px;
            width: 22px; height: 22px; padding: 0; flex: 0 0 auto;
            display: inline-flex; align-items: center; justify-content: center;
          }
          .au-filter-tag button:hover { background: #bfdbfe; }
          .au-filter-tag button svg { display: block; }

          .au-search { display: flex; }
          .au-search .au-control { border-top-right-radius: 0; border-bottom-right-radius: 0; }
          .au-search-btn {
            flex: 0 0 auto; width: 46px; border: 1px solid #004987; background: #004987; color: #fff;
            border-radius: 0 12px 12px 0; display: inline-flex; align-items: center; justify-content: center;
            padding: 0;
          }
          .au-search-btn:hover { background: #003a6c; }
          .au-search-btn:focus-visible { outline: 2px solid #93c5fd; outline-offset: 2px; }
          .au-search-btn svg { display: block; }

          .au-pct { display: flex; align-items: center; gap: 8px; min-width: 140px; }
          .au-pct-track { flex: 1; height: 6px; background: #f1f5f9; border-radius: 99px; overflow: hidden; }
          .au-pct-fill { height: 100%; background: #004987; }

          .au-empty { text-align: center; color: #64748b; padding: 36px 12px; }

          @media (prefers-reduced-motion: reduce) { .au-info::after { transition: none; } }
        `}
      </style>

      <div className="au-page">
        <div className="au-head">
          <div>
            <h1 className="au-title h3">Auditoría de uso</h1>
            <div className="au-subtitle">
              La sesión de Babilonia se queda abierta hasta que el usuario la
              cierra, así que aquí no se cuentan inicios de sesión: se revisa
              la última vez que cada persona hizo algo en el sistema y cuántos
              días del periodo tuvo movimiento. Así se detectan cuentas que ya no
              se usan y agencias donde el sistema casi no se ocupa.
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-success"
              onClick={exportarUsuarios}
              disabled={loading || usuariosFiltrados.length === 0}
            >
              <i className="fa fa-file-excel-o me-2" />
              Exportar
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={limpiarFiltros}
              disabled={loading}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-theme"
              onClick={() => {
                cargarResumen();
                if (tab === "bitacora") cargarBitacora();
              }}
              disabled={loading}
            >
              {loading ? "Cargando..." : "Actualizar"}
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="au-panel">
          <div className="row g-3 align-items-end">
            <div className="col-6 col-md-4 col-xl-2">
              <label className="au-label" htmlFor="au-desde">Desde</label>
              <input
                id="au-desde"
                type="date"
                className="form-control au-control"
                value={fechaInicio}
                max={fechaFin}
                onChange={(e) => {
                  setFechaInicio(e.target.value);
                  setPresetSel("");
                }}
              />
            </div>
            <div className="col-6 col-md-4 col-xl-2">
              <label className="au-label" htmlFor="au-hasta">Hasta</label>
              <input
                id="au-hasta"
                type="date"
                className="form-control au-control"
                value={fechaFin}
                min={fechaInicio}
                max={hoy()}
                onChange={(e) => {
                  setFechaFin(e.target.value);
                  setPresetSel("");
                }}
              />
            </div>
            <div className="col-md-4 col-xl-2">
              <label className="au-label" htmlFor="au-agencia">Agencia</label>
              <select
                id="au-agencia"
                className="form-select au-control"
                value={agencia}
                onChange={(e) => setAgencia(e.target.value)}
              >
                <option value="">Todas las agencias</option>
                {agencias.map((a) => (
                  <option key={a.ag_id} value={String(a.ag_id)}>
                    {a.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4 col-xl-2">
              <label className="au-label" htmlFor="au-estatus">
                Cuentas <Info texto={TIP.estatus} />
              </label>
              <select
                id="au-estatus"
                className="form-select au-control"
                value={estatus}
                onChange={(e) => setEstatus(e.target.value)}
              >
                <option value="activos">Solo activas</option>
                <option value="inactivos">Solo inactivas</option>
                <option value="">Todas</option>
              </select>
            </div>
            <div className="col-6 col-md-4 col-xl-2">
              <label className="au-label" htmlFor="au-ultima">
                Última actividad <Info texto={TIP.ultima} />
              </label>
              <select
                id="au-ultima"
                className="form-select au-control"
                value={filtroUltima}
                onChange={(e) => setFiltroUltima(e.target.value)}
              >
                {Object.entries(FILTROS_ULTIMA).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 col-xl-2">
              <label className="au-label" htmlFor="au-nivel">
                Nivel de uso <Info texto={TIP.nivel} align="right" />
              </label>
              <select
                id="au-nivel"
                className="form-select au-control"
                value={nivel}
                onChange={(e) => setNivel(e.target.value)}
              >
                <option value="">Todos los niveles</option>
                {Object.entries(NIVELES).map(([key, n]) => (
                  <option key={key} value={key}>
                    {n.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="au-presets">
            {PRESETS.map((p) => {
              const activo = presetSel === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`au-chip ${activo ? "au-chip-activo" : ""}`}
                  aria-pressed={activo}
                  onClick={() => aplicarPreset(p.key)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {resumen && resumen.registro_activo === false && (
          <div className="au-notice au-notice-off" role="note">
            El registro de actividad está desactivado en el servidor. Se muestran
            solo los datos que ya estaban guardados; no se están registrando
            acciones nuevas.
          </div>
        )}

        {resumen && (
          <div className="au-notice" role="note">
            {resumen.seguimiento_desde
              ? `La actividad se registra desde el ${formatFechaHora(
                  resumen.seguimiento_desde,
                )}. Quien no ha hecho nada desde entonces aparece como "Sin registro", aunque haya usado el sistema antes de esa fecha.`
              : "Todavía no hay actividad registrada. Los datos aparecerán en cuanto los usuarios abran secciones o inicien sesión."}
          </div>
        )}

        {/* Indicadores */}
        <div className="au-kpis">
          <div className="au-kpi au-kpi-main">
            <div className="au-kpi-label">
              Usuarios con actividad <Info texto={TIP.conActividad} align="left" />
            </div>
            <div className="au-kpi-value">
              {kpis.conActividad} <small>de {kpis.total}</small>
            </div>
            <div className="au-kpi-note">{kpis.pct}% de los usuarios filtrados</div>
          </div>
          <div className={`au-kpi ${kpis.sinActividad > 0 ? "au-kpi-alert" : ""}`}>
            <div className="au-kpi-label">
              Sin actividad en el periodo <Info texto={TIP.sinActividadPeriodo} />
            </div>
            <div className="au-kpi-value">{kpis.sinActividad}</div>
            <div className="au-kpi-note">No hicieron nada entre las fechas elegidas</div>
          </div>
          <div className={`au-kpi ${kpis.sin30 > 0 ? "au-kpi-alert" : ""}`}>
            <div className="au-kpi-label">
              Sin actividad hace 30+ días <Info texto={TIP.sin30} />
            </div>
            <div className="au-kpi-value">{kpis.sin30}</div>
            <div className="au-kpi-note">Cuentas para dar seguimiento o baja</div>
          </div>
          <div className="au-kpi">
            <div className="au-kpi-label">
              Días con actividad <Info texto={TIP.promDias} />
            </div>
            <div className="au-kpi-value">
              {formatPromedio(kpis.promDias)} <small>de {diasPeriodo}</small>
            </div>
            <div className="au-kpi-note">Promedio por usuario con actividad</div>
          </div>
          <div className="au-kpi">
            <div className="au-kpi-label">
              Acciones registradas <Info texto={TIP.acciones} align="right" />
            </div>
            <div className="au-kpi-value">{kpis.acciones.toLocaleString("es-MX")}</div>
            <div className="au-kpi-note">Secciones abiertas, inicios y cierres</div>
          </div>
        </div>

        {/* Gráficas */}
        <div className="au-grid-2">
          <div className="au-panel">
            <h2 className="au-panel-title">
              Usuarios con actividad por día <Info texto={TIP.porDia} />
            </h2>
            {porDia.length === 0 ? (
              <div className="au-empty">Sin datos en el periodo.</div>
            ) : (
              <>
                <div className="au-bars">
                  {porDia.map((d) => (
                    <div
                      className="au-bar-col"
                      key={d.dia}
                      title={`${formatDiaCorto(d.dia)}: ${d.usuarios} usuarios, ${d.acciones} acciones`}
                    >
                      <div
                        className={`au-bar ${d.usuarios === 0 ? "au-bar-zero" : ""}`}
                        style={{ height: `${(d.usuarios / maxDia) * 100}%` }}
                      />
                    </div>
                  ))}
                </div>
                <div className="au-bars-axis">
                  <span>{formatDiaCorto(porDia[0].dia)}</span>
                  <span>Máx. {maxDia} usuarios en un día</span>
                  <span>{formatDiaCorto(porDia[porDia.length - 1].dia)}</span>
                </div>

                <h3 className="au-subhead">
                  Horario de actividad <Info texto={TIP.porHora} />
                </h3>
                <div className="au-hours" aria-label="Actividad por hora del día">
                  {porHora.map((total, hora) => (
                    <div
                      key={hora}
                      className="au-hour"
                      title={`${pad(hora)}:00 a ${pad(hora)}:59: ${total} acciones`}
                      style={{
                        background:
                          total === 0
                            ? "#f1f5f9"
                            : `rgba(0, 73, 135, ${0.15 + (total / maxHora) * 0.85})`,
                      }}
                    />
                  ))}
                </div>
                <div className="au-hours-axis">
                  {porHora.map((_, hora) => (
                    <span key={hora}>{hora % 3 === 0 ? `${hora}h` : ""}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="au-panel">
            <h2 className="au-panel-title">
              Secciones más usadas <Info texto={TIP.topSecciones} align="right" />
            </h2>
            {topSecciones.length === 0 ? (
              <div className="au-empty">Sin secciones abiertas en el periodo.</div>
            ) : (
              topSecciones.map((s) => (
                <div className="au-sec-row" key={s.ruta}>
                  <span>{nombreSeccion(s.ruta)}</span>
                  <span className="au-sec-count">
                    {s.visitas} visitas, {s.usuarios} {s.usuarios === 1 ? "usuario" : "usuarios"}
                  </span>
                  <div className="au-sec-track">
                    <div className="au-sec-fill" style={{ width: `${(s.visitas / maxSeccion) * 100}%` }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Tablas */}
        <div className="au-panel">
          <div className="au-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "usuarios"}
              className={`au-tab ${tab === "usuarios" ? "active" : ""}`}
              onClick={() => setTab("usuarios")}
            >
              Por usuario ({usuariosFiltrados.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "agencias"}
              className={`au-tab ${tab === "agencias" ? "active" : ""}`}
              onClick={() => setTab("agencias")}
            >
              Por agencia ({resumenAgencias.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "bitacora"}
              className={`au-tab ${tab === "bitacora" ? "active" : ""}`}
              onClick={() => setTab("bitacora")}
            >
              Bitácora
            </button>
          </div>

          {tab === "usuarios" && (
            <>
              <div className="row g-2 mb-3">
                <div className="col-md-6 col-xl-4">
                  <input
                    type="text"
                    className="form-control au-control"
                    placeholder="Buscar por nombre, correo o agencia..."
                    value={buscar}
                    onChange={(e) => setBuscar(e.target.value)}
                    aria-label="Buscar usuario"
                  />
                </div>
              </div>
              <div className="au-table-wrap">
                <table className="table au-table">
                  <thead>
                    <tr>
                      <Th campo="nombre" texto sort={orden} onSort={setOrden}>Usuario</Th>
                      <Th campo="agencias_texto" texto sort={orden} onSort={setOrden}>Agencias</Th>
                      <Th campo="nivel_rango" sort={orden} onSort={setOrden} tip={TIP.nivel} align="left">Nivel</Th>
                      <Th campo="dias_sin_actividad_orden" sort={orden} onSort={setOrden} tip={TIP.ultima} align="left">Última actividad</Th>
                      <Th campo="dias_activos" sort={orden} onSort={setOrden} tip={TIP.dias} num>Días con actividad</Th>
                      <Th campo="acciones" sort={orden} onSort={setOrden} tip={TIP.accionesUsuario} num>Acciones</Th>
                      <Th campo="secciones_distintas" sort={orden} onSort={setOrden} tip={TIP.secciones} num>Secciones</Th>
                      <Th campo="logins_ok" sort={orden} onSort={setOrden} tip={TIP.logins} num>Inicios de sesión</Th>
                      <Th campo="logins_fallidos" sort={orden} onSort={setOrden} tip={TIP.fallidos} num align="right">Fallidos</Th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={10} className="au-empty">Cargando...</td>
                      </tr>
                    )}
                    {!loading && usuariosFiltrados.length === 0 && (
                      <tr>
                        <td colSpan={10} className="au-empty">Ningún usuario coincide con los filtros.</td>
                      </tr>
                    )}
                    {!loading &&
                      usuariosFiltrados.map((u) => {
                        const viejo = u.dias_sin_actividad === null || u.dias_sin_actividad >= 30;
                        return (
                          <tr key={u.id}>
                            <td>
                              <div className="au-user">
                                <div className={`au-avatar ${u.activo === 1 ? "" : "au-avatar-off"}`}>
                                  {iniciales(u.nombre)}
                                </div>
                                <div>
                                  <div className="au-user-name">
                                    {u.nombre}
                                    {u.activo !== 1 && <span className="au-muted"> (inactiva)</span>}
                                  </div>
                                  <div className="au-user-mail" title={u.correo}>{u.correo}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="au-agencias" title={u.agenciasNombres.join(", ")}>
                                {u.agenciasNombres.length === 0 ? (
                                  <span className="au-muted">Sin agencia</span>
                                ) : u.agenciasNombres.length <= 2 ? (
                                  u.agenciasNombres.join(", ")
                                ) : (
                                  `${u.agenciasNombres.slice(0, 2).join(", ")} +${u.agenciasNombres.length - 2}`
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`au-pill ${NIVELES[u.nivel].className}`}>
                                {NIVELES[u.nivel].label}
                              </span>
                            </td>
                            <td>
                              <div className={viejo ? "au-alerta" : ""}>{textoHace(u.dias_sin_actividad)}</div>
                              {u.ultima_actividad && (
                                <div className="au-muted" style={{ fontSize: 11 }}>
                                  {formatFechaHora(u.ultima_actividad)}
                                </div>
                              )}
                            </td>
                            <td className="au-num">
                              {u.dias_activos}
                              <span className="au-muted"> de {diasPeriodo}</span>
                            </td>
                            <td className="au-num">{u.acciones}</td>
                            <td className="au-num">{u.secciones_distintas}</td>
                            <td className="au-num">{u.logins_ok}</td>
                            <td className={`au-num ${u.logins_fallidos > 0 ? "au-alerta" : ""}`}>
                              {u.logins_fallidos}
                            </td>
                            <td>
                              <button type="button" className="au-link" onClick={() => verBitacoraDe(u)}>
                                Ver detalle
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === "agencias" && (
            <div className="au-table-wrap">
              <table className="table au-table">
                <thead>
                  <tr>
                    <Th campo="nombre" texto sort={ordenAg} onSort={setOrdenAg} tip={TIP.agenciaMulti} align="left">Agencia</Th>
                    <Th campo="usuarios" sort={ordenAg} onSort={setOrdenAg} num>Usuarios</Th>
                    <Th campo="conActividad" sort={ordenAg} onSort={setOrdenAg} tip={TIP.conActividad} num>Con actividad</Th>
                    <Th campo="pct" sort={ordenAg} onSort={setOrdenAg} tip={TIP.agenciaPct}>% con actividad</Th>
                    <Th campo="sin30" sort={ordenAg} onSort={setOrdenAg} tip={TIP.sin30} num>Sin actividad 30+ días</Th>
                    <Th campo="promDias" sort={ordenAg} onSort={setOrdenAg} tip={TIP.promDias} num>Prom. días con actividad</Th>
                    <Th campo="acciones" sort={ordenAg} onSort={setOrdenAg} tip={TIP.acciones} num align="right">Acciones</Th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {resumenAgencias.length === 0 && (
                    <tr>
                      <td colSpan={8} className="au-empty">Sin datos.</td>
                    </tr>
                  )}
                  {agenciasOrdenadas.map((r) => (
                    <tr key={r.ag_id}>
                      <td style={{ fontWeight: 650 }}>{r.nombre}</td>
                      <td className="au-num">{r.usuarios}</td>
                      <td className="au-num">{r.conActividad}</td>
                      <td>
                        <div className="au-pct">
                          <div className="au-pct-track">
                            <div className="au-pct-fill" style={{ width: `${r.pct}%` }} />
                          </div>
                          <span className="au-num" style={{ minWidth: 38 }}>{r.pct}%</span>
                        </div>
                      </td>
                      <td className={`au-num ${r.sin30 > 0 ? "au-alerta" : ""}`}>{r.sin30}</td>
                      <td className="au-num">
                        {formatPromedio(r.promDias)}
                        <span className="au-muted"> de {diasPeriodo}</span>
                      </td>
                      <td className="au-num">{r.acciones}</td>
                      <td>
                        {r.ag_id !== "__sin__" && agencia === "" && (
                          <button
                            type="button"
                            className="au-link"
                            onClick={() => {
                              setAgencia(r.ag_id);
                              setTab("usuarios");
                            }}
                          >
                            Ver usuarios
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "bitacora" && (
            <>
              <div className="row g-2 mb-3 align-items-center">
                <div className="col-md-4 col-xl-3">
                  <select
                    className="form-select au-control"
                    value={bitEvento}
                    onChange={(e) => setBitEvento(e.target.value)}
                    aria-label="Tipo de acción"
                  >
                    <option value="">Todas las acciones</option>
                    {Object.entries(EVENTOS).map(([key, ev]) => (
                      <option key={key} value={key}>{ev.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-5 col-xl-4">
                  <div className="au-search">
                    <input
                      type="search"
                      enterKeyHint="search"
                      className="form-control au-control"
                      placeholder="Correo, nombre o sección"
                      value={bitBuscar}
                      onChange={(e) => {
                        setBitBuscar(e.target.value);
                        // Si se borra el texto, se quita el filtro de inmediato
                        if (e.target.value === "") setBitBuscarAplicado("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setBitBuscarAplicado(bitBuscar.trim());
                      }}
                      aria-label="Buscar en bitácora"
                    />
                    <button
                      type="button"
                      className="au-search-btn"
                      onClick={() => setBitBuscarAplicado(bitBuscar.trim())}
                      aria-label="Buscar"
                      title="Buscar"
                    >
                      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                        <circle cx="8.5" cy="8.5" r="5.75" fill="none" stroke="currentColor" strokeWidth="2" />
                        <path d="M13 13 L17.5 17.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="col-md-3 col-xl-5 d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
                  {bitUsuario && (
                    <span className="au-filter-tag">
                      {bitUsuario.nombre}
                      <button type="button" aria-label="Quitar filtro de usuario" onClick={() => setBitUsuario(null)}>
                        <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
                          <path d="M1.5 1.5 L8.5 8.5 M8.5 1.5 L1.5 8.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </button>
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={exportarBitacora}
                    disabled={bitLoading || bitacora.total === 0}
                  >
                    Exportar bitácora
                  </button>
                </div>
              </div>

              <div className="au-table-wrap">
                <table className="table au-table">
                  <thead>
                    <tr>
                      <Th campo="fecha" sort={ordenBit} onSort={setOrdenBit} tip={TIP.bitacora} align="left">Fecha</Th>
                      <Th campo="usuario" texto sort={ordenBit} onSort={setOrdenBit}>Usuario</Th>
                      <Th campo="evento" texto sort={ordenBit} onSort={setOrdenBit}>Acción</Th>
                      <Th campo="detalle" texto sort={ordenBit} onSort={setOrdenBit}>Detalle</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {bitLoading && (
                      <tr>
                        <td colSpan={4} className="au-empty">Cargando...</td>
                      </tr>
                    )}
                    {!bitLoading && bitacora.eventos.length === 0 && (
                      <tr>
                        <td colSpan={4} className="au-empty">No hay acciones con estos filtros.</td>
                      </tr>
                    )}
                    {!bitLoading &&
                      bitacora.eventos.map((e) => {
                        const ev = EVENTOS[e.evento] || { label: e.evento, className: "au-ev-out" };
                        return (
                          <tr key={e.id}>
                            <td style={{ whiteSpace: "nowrap" }}>{formatFechaHora(e.created_at)}</td>
                            <td>
                              <div className="au-user-name" style={{ fontWeight: 650 }}>
                                {e.nombre || <span className="au-muted">No registrado</span>}
                              </div>
                              <div className="au-user-mail">{e.correo}</div>
                            </td>
                            <td>
                              <span className={`au-pill ${ev.className}`}>{ev.label}</span>
                            </td>
                            <td>
                              {e.evento === "navegacion"
                                ? nombreSeccion(e.detalle)
                                : e.detalle || <span className="au-muted">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
                <span className="au-muted" style={{ fontSize: 13 }}>
                  {bitacora.total.toLocaleString("es-MX")} acciones
                </span>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={bitPagina === 0 || bitLoading}
                    onClick={() => setBitPagina((p) => Math.max(0, p - 1))}
                  >
                    Anterior
                  </button>
                  <span style={{ fontSize: 13 }}>Página {bitPagina + 1} de {totalPaginas}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={bitPagina + 1 >= totalPaginas || bitLoading}
                    onClick={() => setBitPagina((p) => p + 1)}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default BabiloniaAuditoriaAdmin;